/**
 * Focus Workspace Manager
 * Manages focus mode panels and layout state
 */
export class FocusWorkspaceManager {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      persistPanels: true,
      ...options,
    };
    this.activePanels = []; // Array of active panel IDs
    this.maxPanels = 3;
    this.panelComponents = new Map(); // Store panel instances

    // Available panel configurations
    this.availablePanels = [
      { id: 'brain-dump', label: 'Brain Dump', icon: '🧠', component: null },
      { id: 'capacity', label: 'Capacity', icon: '⚡', component: null },
      { id: 'projects', label: 'Projects', icon: '📋', component: null },
      { id: 'values', label: 'Values', icon: '🎯', component: null },
      { id: 'calendar', label: 'Schedule', icon: '📅', component: null }
    ];

    this.init();
  }

  init() {
    this.loadState();
    this.setupKeyboardShortcuts();
    this.render();
  }

  loadState() {
    if (!this.options.persistPanels) {
      this.activePanels = [];
      this.clearPersistedState();
      return;
    }

    try {
      const saved = localStorage.getItem('lifeDashboard_v2_focusPanels');
      if (saved) {
        const state = JSON.parse(saved);
        this.activePanels = state.activePanels || [];
      }
    } catch (err) {
      console.warn('Failed to load focus panel state:', err);
    }
  }

  saveState() {
    if (!this.options.persistPanels) {
      return;
    }

    try {
      localStorage.setItem('lifeDashboard_v2_focusPanels', JSON.stringify({
        activePanels: this.activePanels,
        timestamp: Date.now()
      }));
    } catch (err) {
      console.warn('Failed to save focus panel state:', err);
    }
  }

  setupKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      // Cmd/Ctrl + Number to toggle panels
      if ((e.metaKey || e.ctrlKey) && e.key >= '1' && e.key <= '5') {
        e.preventDefault();
        const index = parseInt(e.key) - 1;
        const panel = this.availablePanels[index];
        if (panel) {
          this.togglePanel(panel.id);
        }
      }
    });
  }

  togglePanel(panelId) {
    const index = this.activePanels.indexOf(panelId);

    if (index > -1) {
      // Remove panel
      this.removePanel(panelId);
    } else {
      // Add panel (if under max)
      this.addPanel(panelId);
    }
  }

  addPanel(panelId) {
    if (this.activePanels.length >= this.maxPanels) {
      console.warn(`Maximum ${this.maxPanels} panels reached`);
      return false;
    }

    if (!this.activePanels.includes(panelId)) {
      this.activePanels.push(panelId);
      this.saveState();
      this.render();
      return true;
    }
    return false;
  }

  removePanel(panelId) {
    const index = this.activePanels.indexOf(panelId);
    if (index > -1) {
      this.activePanels.splice(index, 1);
      this.saveState();
      this.render();
      return true;
    }
    return false;
  }

  getLayoutClass() {
    const count = this.activePanels.length;
    if (count === 0) return 'layout-empty';
    if (count === 1) return 'layout-single';
    if (count === 2) return 'layout-split-2';
    if (count === 3) return 'layout-split-3';
    return 'layout-single';
  }

  render() {
    // Update container class
    const layoutClass = this.getLayoutClass();
    this.container.className = `focus-workspace ${layoutClass}`;
    this.container.dataset.panelCount = String(this.activePanels.length);
    this.container.dataset.layout = layoutClass;

    // Clear container
    this.container.innerHTML = '';

    if (this.activePanels.length === 0) {
      this.renderEmptyState();
      return;
    }

    // Render active panels
    this.activePanels.forEach(panelId => {
      const panelConfig = this.availablePanels.find(p => p.id === panelId);
      if (panelConfig) {
        this.renderPanel(panelConfig);
      }
    });

    // Dispatch event for other components to react
    this.container.dispatchEvent(new CustomEvent('workspace-changed', {
      detail: {
        activePanels: this.activePanels,
        layout: layoutClass
      },
      bubbles: true
    }));
  }

  renderEmptyState() {
    this.container.innerHTML = `
      <div class="empty-workspace">
        <div class="empty-workspace__content">
          <div class="empty-workspace__icon">🎯</div>
          <h2>What do you want to focus on?</h2>
          <p>Choose from the sidebar to add a workspace</p>
          <div class="empty-workspace__hints">
            <span class="hint">💡 Tip: Use <kbd>⌘1</kbd>-<kbd>⌘5</kbd> for quick access</span>
          </div>
        </div>
      </div>
    `;
  }

  renderPanel(panelConfig) {
    const panel = document.createElement('div');
    panel.className = 'focus-panel';
    panel.dataset.panelId = panelConfig.id;

    const canClose = this.activePanels.length > 1;

    panel.innerHTML = `
      <div class="focus-panel__header">
        <div class="focus-panel__title">
          <span class="focus-panel__icon">${panelConfig.icon}</span>
          <h3>${panelConfig.label}</h3>
        </div>
        <div class="focus-panel__actions">
          ${canClose ? `
            <button class="focus-panel__close" data-action="close" aria-label="Close panel">
              ✕
            </button>
          ` : ''}
        </div>
      </div>
      <div class="focus-panel__body" id="panel-${panelConfig.id}">
        <!-- Component content will be inserted here -->
        <div class="panel-loading">Loading ${panelConfig.label}...</div>
      </div>
    `;

    // Add event listeners
    const closeBtn = panel.querySelector('[data-action="close"]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.removePanel(panelConfig.id));
    }

    this.container.appendChild(panel);

    // Emit event so components can mount themselves
    panel.dispatchEvent(new CustomEvent('panel-mounted', {
      detail: { panelId: panelConfig.id },
      bubbles: true
      }));
  }

  // Register a panel component for lazy loading
  registerPanel(panelId, componentInstance) {
    this.panelComponents.set(panelId, componentInstance);
  }

  isPanelActive(panelId) {
    return this.activePanels.includes(panelId);
  }

  // Get the container element for a specific panel
  getPanelContainer(panelId) {
    return this.container.querySelector(`#panel-${panelId}`);
  }

  clearPersistedState() {
    if (!this.options.persistPanels) {
      try {
        localStorage.removeItem('lifeDashboard_v2_focusPanels');
      } catch (err) {
        console.warn('Failed to clear focus panel persistence:', err);
      }
    }
  }
}
